import React from "react";
import { Link } from "react-router-dom";
import { FileText, Hammer } from "lucide-react";
import { Badge } from "./IntakeStat";
import { fmtDate, fmtMoney } from "./adminUtils";

/** A single intake/submission row card. Tone shifts olive/clay once Stripe
 *  confirms. Behavior + testids preserved verbatim from the inline version. */
export default function IntakeRow({ it, cfg, division, onKickoff }) {
  const rowTone =
    it.status === "paid"
      ? "bg-olive/5"
      : it.status === "in_build"
        ? "bg-clay/5"
        : "bg-transparent";

  return (
    <div
      data-testid={`admin-row-${it.id}`}
      className={`px-4 sm:px-6 py-4 sm:py-5 border-b border-sand-300 last:border-b-0 ${rowTone}`}
    >
      {/* Header: business + identity (left) + badge (right) */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14.5px] font-medium text-ink leading-tight truncate">
            {it.business_name || "—"}
          </p>
          <p className="mt-1 text-[12.5px] text-ink-muted truncate">
            {it.full_name || "—"}
            {it.email && (
              <>
                <span className="text-ink-muted/50"> · </span>
                <a
                  href={`mailto:${it.email}`}
                  className="hover:text-clay transition-colors break-all"
                >
                  {it.email}
                </a>
              </>
            )}
          </p>
          {it.phone && (
            <p className="mt-0.5 text-[11.5px] text-ink-muted/80">
              <a href={`tel:${it.phone}`} className="hover:text-clay transition-colors">
                {it.phone}
              </a>
            </p>
          )}
        </div>
        <div className="flex-shrink-0">
          <Badge status={it.status} />
        </div>
      </div>

      {/* Compact grid: Package + Amount, plus optional launch + session */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
        <div className="min-w-0">
          <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ink-muted">
            Package
          </p>
          <p className="mt-1 text-[13px] text-ink leading-snug">
            {cfg.packageLabels[it.package_selected] ||
              it.package_selected ||
              it.package_key ||
              "—"}
          </p>
          {division === "websites" && it.ideal_launch_date && (
            <p className="mt-1 text-[11px] text-ink-muted">
              Launch · {it.ideal_launch_date}
            </p>
          )}
          {division === "revenue" && it.ideal_start_date && (
            <p className="mt-1 text-[11px] text-ink-muted">
              Start · {it.ideal_start_date}
            </p>
          )}
          {division === "revenue" && it.monthly_leads && (
            <p className="mt-0.5 text-[11px] text-ink-muted">
              Leads/mo · {it.monthly_leads}
            </p>
          )}
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ink-muted">
            Amount
          </p>
          <p className="mt-1 text-[13px] text-ink tabular-nums">
            {fmtMoney(it.stripe_amount_total, it.stripe_currency)}
          </p>
          {it.stripe_session_id && (
            <p className="mt-1 text-[10px] font-mono text-ink-muted/80 truncate">
              {it.stripe_session_id.slice(0, 14)}…
            </p>
          )}
          {division === "revenue" && it.ghl_contact_id && (
            <p
              className="mt-1 text-[10px] font-mono text-olive/90 truncate"
              title="Pushed to GoHighLevel"
            >
              GHL · {it.ghl_contact_id.slice(0, 10)}…
            </p>
          )}
        </div>
        <div className="col-span-2 lg:col-span-1 min-w-0">
          <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ink-muted">
            Submitted
          </p>
          <p className="mt-1 text-[12px] font-mono text-ink-muted">
            {fmtDate(it.kicked_off_at || it.paid_at || it.created_at)}
          </p>
        </div>
      </div>

      {/* Action footer */}
      <div className="mt-4 pt-4 border-t border-sand-300/70 flex items-center gap-2 flex-wrap">
        {division === "websites" && cfg.detailRoute && (
          <Link
            to={cfg.detailRoute(it.id)}
            data-testid={`admin-view-${it.id}`}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-sand-300 bg-sand-100 px-3.5 py-2 text-[12px] text-ink hover:border-clay/50 transition-colors min-h-[36px] flex-1 sm:flex-initial"
          >
            <FileText size={11} /> View + brief
          </Link>
        )}
        {division === "revenue" && it.biggest_leak && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-sand-100 px-3.5 py-2 text-[11.5px] text-ink-muted min-h-[36px] flex-1 sm:flex-initial truncate max-w-full"
            data-testid={`admin-leak-${it.id}`}
            title={it.biggest_leak}
          >
            <FileText size={11} className="shrink-0" />
            <span className="truncate">Leak · {it.biggest_leak}</span>
          </span>
        )}
        {cfg.supportsKickoff && it.status === "paid" && (
          <button
            onClick={() => onKickoff(it.id)}
            data-testid={`admin-kickoff-${it.id}`}
            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-ink-deep text-sand-100 px-3.5 py-2 text-[12px] font-medium hover:bg-rust transition-colors min-h-[36px] flex-1 sm:flex-initial"
          >
            <Hammer size={11} /> Mark as kicked off
          </button>
        )}
      </div>
    </div>
  );
}
